import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AnalyticsCatalogService } from '../analytics-catalog.service';
import { ChatSessionSummary } from '../models/analytics-catalog.models';

/**
 * "Archive" tab: archived chat sessions of the current user. Sessions can be restored to the
 * sidebar, opened in the chat, or deleted permanently.
 */
@Component({
  selector: 'app-archive-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  styleUrls: ['./catalog-tab.shared.scss'],
  template: `
    <section class="catalog-panel" data-tour="archive-panel">
      <div class="panel-head">
        <div>
          <h2>{{ 'reporting_analytics.archive_title' | translate }}</h2>
          <span class="muted">{{ 'reporting_analytics.archive_hint' | translate }}</span>
        </div>
        <div class="filters">
          <input
            type="search"
            [ngModel]="search"
            (ngModelChange)="onSearch($event)"
            [placeholder]="'reporting_analytics.search' | translate" />
          <button type="button" class="btn" (click)="load()" [disabled]="loading">
            <i class="pi pi-refresh" [class.pi-spin]="loading"></i>
            {{ 'reporting_analytics.refresh' | translate }}
          </button>
        </div>
      </div>

      <div class="alert" *ngIf="error">{{ error }}</div>

      <div class="catalog-table-wrap" *ngIf="items.length; else empty">
        <table class="catalog-table">
          <thead>
            <tr>
              <th>{{ 'reporting_analytics.col_title' | translate }}</th>
              <th>{{ 'reporting_analytics.col_messages' | translate }}</th>
              <th>{{ 'reporting_analytics.col_created' | translate }}</th>
              <th>{{ 'reporting_analytics.col_updated' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let session of items">
              <td class="title-cell" [title]="session.title || session.session_id">
                <i class="pi pi-star-fill star" *ngIf="session.starred"></i>
                {{ session.title || ('reporting_analytics.untitled_session' | translate) }}
              </td>
              <td class="muted">{{ session.message_count }}</td>
              <td class="muted">{{ session.created_at_utc | date: 'dd.MM.yyyy HH:mm' }}</td>
              <td class="muted">{{ session.updated_at_utc | date: 'dd.MM.yyyy HH:mm' }}</td>
              <td class="actions">
                <button type="button" class="btn" (click)="openSession.emit(session.session_id)">
                  <i class="pi pi-comments"></i> {{ 'reporting_analytics.action_open' | translate }}
                </button>
                <button type="button" class="btn icon" [title]="'reporting_analytics.action_restore' | translate" [disabled]="busyId === session.session_id" (click)="restore(session)">
                  <i class="pi pi-undo"></i>
                </button>
                <button type="button" class="btn icon danger" [title]="'reporting_analytics.action_delete' | translate" [disabled]="busyId === session.session_id" (click)="remove(session)">
                  <i class="pi pi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ng-template #empty>
        <div class="empty" *ngIf="!loading">
          <i class="pi pi-inbox"></i>
          <p>{{ 'reporting_analytics.no_archived_sessions' | translate }}</p>
        </div>
      </ng-template>
    </section>
  `,
  styles: [`
    .star { margin-right: 6px; color: #ffbb32; font-size: 12px; }
  `]
})
export class ArchiveTabComponent implements OnInit, OnDestroy {
  @Output() openSession = new EventEmitter<string>();

  items: ChatSessionSummary[] = [];
  search = '';
  loading = false;
  busyId: string | null = null;
  error: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();

  constructor(
    private readonly catalog: AnalyticsCatalogService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
    this.search$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(value: string): void {
    this.search = value;
    this.search$.next(value);
  }

  load(): void {
    this.loading = true;
    this.catalog
      .listSessions({ archived: 'true', search: this.search, take: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.items = items;
          this.loading = false;
          this.error = null;
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.error = err?.error?.error || err?.message || this.translate.instant('reporting_analytics.request_failed');
        }
      });
  }

  restore(session: ChatSessionSummary): void {
    this.busyId = session.session_id;
    this.catalog
      .updateSession(session.session_id, { archived: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId = null;
          this.items = this.items.filter((s) => s.session_id !== session.session_id);
        },
        error: () => {
          this.busyId = null;
          this.load();
        }
      });
  }

  remove(session: ChatSessionSummary): void {
    if (!confirm(this.translate.instant('reporting_analytics.confirm_delete_session'))) {
      return;
    }
    this.busyId = session.session_id;
    this.catalog
      .deleteSession(session.session_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId = null;
          this.items = this.items.filter((s) => s.session_id !== session.session_id);
        },
        error: () => {
          this.busyId = null;
          this.load();
        }
      });
  }
}
